import { defineComponent } from "vue";
import { useRoute } from "vue-router";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";

import { useApp } from "../App/useApp";
import { useDraftsStore } from "./store/useDraftsStore";
import { useConversationStore } from "./store/useConversationStore";
import centralIntakeRoutes from "./central-messaging-routes";
import {
    IConversationGroups,
    IThreadListFiltersRequest
} from "../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import { MessageService } from "../../services/message-service";
import { FamilyPortalService } from "../../services/family-portal-service";
import {
    ChatNavOptionsEnum,
    ConversationSortEnum,
    ParentPermissionEnum,
    PermissionActionEnum
} from "../../enums/enums";
import { PermissionManager } from "../../common/utilities/permission-manager";
import eventBus from "../../utilities/eventBus";
import { isMobileScreenSize } from "../../utilities/isMobileScreenSize";
import { AnalyticsCategory, AnalyticsService } from "../../services/analytics.service";
import { helperTestingAgency } from "../../common/vue-helpers/testing-agency-check";

import PageLayout from "../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import ScrollPagination from "../../resources-vue/vue-elements/ScrollPagination/ScrollPagination.vue";
import ConversationNav from "./ConversationNav/ConversationNav.vue";
import CallHistory from "./CallHistory/CallHistory.vue";
import MeetingCalendar from "./MeetingCalendar/MeetingCalendar.vue";
import FamilyPortal from "./FamilyPortal/FamilyPortal.vue";
import CallSearch from "./CallSearch/CallSearch.vue";

export default defineComponent({
    components: {
        PageLayout,
        ConversationNav,
        ScrollPagination,
        CallHistory,
        MeetingCalendar,
        FamilyPortal,
        CallSearch
    },

    data() {
        return {
            _messageService: null as MessageService,
            _familyPortalService: null as FamilyPortalService,
            _permissionManager: null as PermissionManager,
            _analyticsService: null as AnalyticsService,
            router: null as any,
            routes: null as any,
            route: useRoute(),
            replaceRouterURL: false as boolean,
            isLoading: false as boolean,
            hideNav: false as boolean,
            isMobile: false as boolean,
            hasError: false,
            conversationUsersType: "clinicians" as string,
            newConversation: false as boolean,
            userOs: "" as string,
            agencyNavOpened: false as boolean,
            activeChatNav: ChatNavOptionsEnum.Chat as ChatNavOptionsEnum,
            chatSearchTerm: "" as string,
            sortBy: ConversationSortEnum.Recent,
            ChatNavOptionsEnum,
            ChatNavOptions: ChatNavOptionsEnum,
            totalUnreadThreadCount: 0 as number,
            conversationGroupsCount: null as IConversationGroups,
            hasFamilyPortalPermission: false as boolean,
            hasTestAgency: false as boolean,
            permissions: {
                Scheduling: false,
                Clinical: false,
                Billing: false,
                Others: false
            }
        };
    },

    computed: {
        ...mapState(useApp, ["userSubId"]),
        ...mapState(useConversationStore, [
            "unreadConvIds",
            "activeConversationId",
            "orderedConvIds",
            "convPagination",
            "showFilteredConvs"
        ]),

        allPagesLoaded(): boolean {
            return (
                !!this.convPagination.totalPageCount &&
                this.convPagination.currentPage > this.convPagination.totalPageCount
            );
        }
    },

    async mounted() {
        this.router = centralIntakeRoutes;
        eventBus.emit("agencyNavOpened", true);
        this.checkPermission();
        this.getThreadList();
        this.loadDrafts();
        this.isMobile = isMobileScreenSize();
    },

    watch: {
        $route: {
            async handler(newValue) {
                this.hideNav = this.$route.path?.length > 12;

                if (newValue.path.includes("new-conversation")) {
                    this.newConversation = true;
                } else if (
                    this.activeConversationId !== newValue.params.id &&
                    newValue.path.includes("/central-messaging")
                ) {
                    // updating conversation details if user navigates through prev and next buttons
                    this.setActiveConversationId(newValue.params.id);
                }

                // Will be fixed in nex ticket
                // if (oldValue?.query.tab === "Call") {
                //     // navigation to a chat through Call History page
                //     // If conversation exists -> load conversation, else fetch more conversations
                //     const navigationId = newValue.params.id;
                //     do {
                //         this.convPagination.currentPage++;
                //         this.chatSearchTerm = "";
                //         await this.fetchConversations(
                //             this.chatSearchTerm,
                //             this.sortBy,
                //             this.convPagination.currentPage,
                // 20,
                // "append"
                //         );

                //         // Find matching conversation after updating
                //         if (this.orderedConvIds.includes(navigationId)) {
                //             this.setActiveConversationId(navigationId);
                //             const element = document.getElementById(
                //                 `conversation-${this.conversations[navigationId].id}`
                //             );
                //             element?.scrollIntoView({ behavior: "smooth" });
                //             break;
                //         }
                //     } while (this.convPagination.currentPage < this.convPagination.totalPageCount);
                // } else
            },
            immediate: true
        },
        async activeConversationId(newId, oldId) {
            if (newId === "newConversation")
                this.$router.push({
                    path: `/central-messaging/clinicians/new-conversation`,
                    replace: true,
                    force: true
                });
            else if (newId) {
                if (this.replaceRouterURL) {
                    this.$router.push({
                        path: `/central-messaging/clinicians/conversation/${newId}?tab=${ChatNavOptionsEnum.Chat}`,
                        replace: true,
                        force: true
                    });
                    this.replaceRouterURL = false;
                } else {
                    this.$router.push(
                        `/central-messaging/clinicians/conversation/${newId}?tab=${ChatNavOptionsEnum.Chat}`
                    );
                }
            }
        }
    },

    async created() {
        this._messageService = Container.instance.get(MessageService);
        this._familyPortalService = Container.instance.get(FamilyPortalService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
        await this.handleFetchConv();

        eventBus.on("replaceRouterURL", (value: boolean) => (this.replaceRouterURL = value));

        eventBus.on("updateActiveChatNav", (chatNavOption: ChatNavOptionsEnum) => (this.activeChatNav = chatNavOption));
        eventBus.on("updateNewConversation", (value: boolean) => (this.newConversation = value));
        eventBus.on("conversationRead", this.handleConversationRead);
        this.userOs = window.sessionStorage.getItem("userOs");
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
    },

    methods: {
        ...mapActions(useConversationStore, ["fetchConversations", "setActiveConversationId", "setConvPagination"]),
        ...mapActions(useDraftsStore, ["loadDrafts"]),

        pageNumberUpdated(pageToLoad: number) {
            if (!pageToLoad) {
                return;
            }
            if (pageToLoad != this.convPagination.currentPage) {
                this.setConvPagination(pageToLoad);
            } else {
                return;
            }
            if (!this.allPagesLoaded) {
                this.handleFetchConv();
            }
        },

        async handleFetchConv(actionType: "replace" | "append" = "append") {
            try {
                this.isLoading = true;
                await this.fetchConversations(this.chatSearchTerm, this.sortBy, actionType);
            } catch (error) {
                console.error(error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async updateChatSearchTerm(value: string) {
            this.chatSearchTerm = value;
            this.setConvPagination(1);
            this.handleFetchConv("replace");
        },

        async updateSortBy(value: ConversationSortEnum) {
            this.sortBy = value;
            this.setConvPagination(1);
            this.handleFetchConv("replace");
        },

        toggleAgencyNav() {
            this.agencyNavOpened = !this.agencyNavOpened;
            eventBus.emit("agencyNavOpened", this.agencyNavOpened);
        },

        chatNavSelected(option: ChatNavOptionsEnum) {
            if (option === ChatNavOptionsEnum.FamilyPortal) {
                this._analyticsService.logEvent({
                    category: AnalyticsCategory.FamilyPortal,
                    action: "Family Portal Icon"
                });
            }
            this.activeChatNav = option;
            this.$router.replace({ query: { ...this.route.query, tab: option } });
        },

        async getThreadList() {
            try {
                const filters: IThreadListFiltersRequest = {
                    agencyIds: [],
                    locationIds: [],
                    patientIds: []
                };
                const response = await this._familyPortalService.getThreadsList(filters);
                this.conversationGroupsCount = response.conversationGroups;
                this.totalUnreadThreadCount = Object.entries(this.conversationGroupsCount).reduce(
                    (sum: number, [key, value]: [string, number]) => {
                        return this.permissions[key] ? sum + (value || 0) : sum;
                    },
                    0
                );
            } catch (error) {
                console.error(error);
            }
        },

        handleConversationRead() {
            this.totalUnreadThreadCount = this.totalUnreadThreadCount - 1;
        },

        checkPermission() {
            const isFPBillingView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalBilling,
                PermissionActionEnum.canView
            );
            const isFPClinicalView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalClinical,
                PermissionActionEnum.canView
            );
            const isFPSchedulingView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalScheduling,
                PermissionActionEnum.canView
            );
            const isFPOthersView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalOthers,
                PermissionActionEnum.canView
            );

            this.permissions = {
                Billing: isFPBillingView,
                Clinical: isFPClinicalView,
                Scheduling: isFPSchedulingView,
                Others: isFPOthersView
            };

            this.hasFamilyPortalPermission =
                isFPBillingView || isFPClinicalView || isFPSchedulingView || isFPOthersView;
        }
    },

    unmounted() {
        eventBus.off("agencyNavOpened");
        eventBus.off("conversationRead", this.handleConversationRead);
        eventBus.off("replaceRouterURL");
        eventBus.off("updateActiveChatNav");
        eventBus.off("updateNewConversation");
        this.setActiveConversationId(null);
    }
});
